// Intestazione di un EMOM: quanti round e quanto dura ognuno.
//
// Unica fonte per forza e conditioning. Prima ogni superficie se la
// ricostruiva da sola e lo stesso blocco usciva "10 set · E1:00" lato forza e
// "10 rounds · ogni 1 minuto" lato conditioning: due scritture diverse per la
// stessa identica prescrizione.
//
// La terminologia è "round" (come in emomRoundLabels) e la cadenza è sempre in
// formato orologio M:SS, perché è quello che l'atleta legge sul timer.

const WORDS = {
	every: { key: "pbEvery", it: "ogni", en: "every" },
	round: { key: "roundLabel", it: "round", en: "round" },
	rounds: { key: "roundsLabel", it: "rounds", en: "rounds" },
};

// Il secondo argomento è la `t` di chi rende, oppure la sola lingua per le
// viste che hanno quella e non il contesto (stessa convenzione di
// programBuilderUtils). Senza nessuno dei due si cade sull'italiano.
const word = (which, tOrLanguage) => {
	const entry = WORDS[which];
	if (typeof tOrLanguage === "function") return tOrLanguage(entry.key);
	return tOrLanguage === "en" ? entry.en : entry.it;
};

/** "1:30" → 90, 90 → 90, "90" → 90. Null quando non è una durata. */
export function parseEmomIntervalSeconds(value) {
	if (value === null || value === undefined) return null;
	if (typeof value === "number") {
		return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
	}
	const raw = String(value).trim();
	if (!raw) return null;
	if (raw.includes(":")) {
		const [minutePart, secondPart] = raw.split(":");
		const minutes = parseInt(minutePart, 10) || 0;
		const seconds = parseInt(secondPart, 10) || 0;
		const total = minutes * 60 + seconds;
		return total > 0 ? total : null;
	}
	const numeric = parseInt(raw.replace(/[^\d]/g, ""), 10);
	return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

/**
 * La durata del singolo round, da qualunque campo la tenga il tipo di
 * esercizio: `emom_interval` (forza), `interval_seconds` (conditioning
 * salvato), `emom_conditioning_interval` (conditioning in bozza).
 */
export function getEmomIntervalSeconds(exercise) {
	if (!exercise || typeof exercise !== "object") return null;
	return (
		parseEmomIntervalSeconds(exercise.emom_interval) ??
		parseEmomIntervalSeconds(exercise.interval_seconds) ??
		parseEmomIntervalSeconds(exercise.emom_conditioning_interval)
	);
}

/**
 * Quanti round. `emom_intervals` lato forza, `rounds` lato conditioning —
 * quest'ultimo può contenere testo libero ("1/1", "10-9-8"), che non è un
 * conteggio e non va mostrato come tale.
 */
export function getEmomRoundCount(exercise) {
	if (!exercise || typeof exercise !== "object") return null;
	const raw = exercise.emom_intervals ?? exercise.rounds;
	const normalized = String(raw ?? "").trim();
	if (!/^\d+$/.test(normalized)) return null;
	const count = Number(normalized);
	return count > 0 ? count : null;
}

/** Secondi → "1:00". Stringa vuota quando non c'è una durata valida. */
export function formatEmomIntervalClock(seconds) {
	const total = Number(seconds);
	if (!Number.isFinite(total) || total <= 0) return "";
	const minutes = Math.floor(total / 60);
	const rest = total % 60;
	return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** ["10 round", "ogni 1:00"] — i pezzi, per chi li rende come badge. */
export function getEmomStructureParts(exercise, t) {
	const parts = [];
	const rounds = getEmomRoundCount(exercise);
	if (rounds) {
		parts.push(`${rounds} ${word(rounds === 1 ? "round" : "rounds", t)}`);
	}
	const clock = formatEmomIntervalClock(getEmomIntervalSeconds(exercise));
	if (clock) parts.push(`${word("every", t)} ${clock}`);
	return parts;
}

/** "10 round · ogni 1:00" — senza la sigla EMOM. */
export function formatEmomStructure(exercise, t) {
	return getEmomStructureParts(exercise, t).join(" · ");
}

/** "EMOM · 10 round · ogni 1:00" — la riga completa. */
export function formatEmomHeadline(exercise, t) {
	const structure = formatEmomStructure(exercise, t);
	return structure ? `EMOM · ${structure}` : "EMOM";
}

/**
 * Il nome mostrato quando il coach non ne scrive uno: la sola sigla, come fa
 * il conditioning. Round e cadenza stanno già nella riga sotto al titolo, e
 * ripeterli lì dentro faceva solo troncare la card.
 */
export function buildEmomAutoName() {
	return "EMOM";
}

// Riconosce i nomi che generiamo noi (anche quelli vecchi, "EMOM 10x E1:00"):
// serve a capire se il titolo è ancora automatico e quindi va riallineato
// quando il coach cambia round o cadenza. I pattern sono stretti di proposito:
// un titolo scritto a mano ("EMOM gambe", "EMOM 12 minuti") non deve finirci
// dentro e non va mai sovrascritto.
const AUTO_NAME_PATTERNS = [
	/^EMOM$/i,
	/^EMOM \d+\s*x\s*E?\d+(?::\d{1,2})?$/i,
	/^EMOM \d+ rounds? · (?:ogni|every) \d+:\d{2}$/i,
	/^EMOM \d+ rounds?$/i,
	/^EMOM (?:ogni|every) \d+:\d{2}$/i,
];

export function isEmomAutoName(name) {
	const normalized = String(name || "")
		.replace(/\s+/g, " ")
		.trim();
	if (!normalized) return true;
	return AUTO_NAME_PATTERNS.some((pattern) => pattern.test(normalized));
}
